import type { FC } from 'react';

import { DocumentTextIcon } from '@heroicons/react/24/outline';

import { CommunityWebPostThumbnail } from '../../../communityWebPostEmbedCard/CommunityWebPostThumbnail';
import type { ComposerAttachablePost } from '../../CommunityPostComposer.type';
import { ICON_CLASS, tr } from '../constants';
import { PanelPopover } from './PanelPopover';
import { PanelSearchInput } from './PanelSearchInput';

interface PostsPanelProps {
  translations?: Record<string, string>;
  posts: ComposerAttachablePost[];
  onPostQueryChange: (query: string) => void;
  isLoadingPosts?: boolean;
  attachedPosts: ComposerAttachablePost[];
  onAttachedPostsChange: (posts: ComposerAttachablePost[]) => void;
  maxAttachedPosts: number;
  transformImageUrl?: (url: string, width: number) => string;
}

export const PostsPanel: FC<PostsPanelProps> = ({
  translations,
  posts,
  onPostQueryChange,
  isLoadingPosts,
  attachedPosts,
  onAttachedPostsChange,
  maxAttachedPosts,
  transformImageUrl,
}) => {
  const isAtCapacity = attachedPosts.length >= maxAttachedPosts;
  const attachedIds = new Set(attachedPosts.map((p) => p.id));

  const handleSelect = (post: ComposerAttachablePost, close: () => void) => {
    if (isAtCapacity || attachedIds.has(post.id)) return;
    onAttachedPostsChange([...attachedPosts, post]);
    close();
  };

  return (
    <PanelPopover
      triggerLabel={tr(translations, 'community.composer.attach_post')}
      triggerIcon={<DocumentTextIcon className={ICON_CLASS} aria-hidden="true" />}
      panelClassName="w-96"
    >
      {({ close }) => (
        <>
          <PanelSearchInput
            onChange={onPostQueryChange}
            placeholder={tr(translations, 'community.composer.post_search_placeholder')}
            ariaLabel={tr(translations, 'community.composer.post_search_placeholder')}
            autoFocus
          />
          {isAtCapacity && (
            <p className="text-xs text-black/50 px-3 py-2 border-b border-black/5">
              {tr(translations, 'community.composer.post_max_reached')}
            </p>
          )}
          <div className="max-h-80 overflow-y-auto p-1">
            {isLoadingPosts ? (
              <p className="text-sm text-black/50 text-center py-6">
                {tr(translations, 'community.composer.post_loading')}
              </p>
            ) : posts.length === 0 ? (
              <p className="text-sm text-black/50 text-center py-6">
                {tr(translations, 'community.composer.post_no_results')}
              </p>
            ) : (
              <ul className="flex flex-col">
                {posts.map((post) => {
                  const disabled = isAtCapacity || attachedIds.has(post.id);
                  const author = post.authors?.[0];
                  return (
                    <li key={post.id}>
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => handleSelect(post, close)}
                        className="w-full flex items-start gap-3 p-2 rounded-xl text-left transition-colors hover:bg-black/5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      >
                        <CommunityWebPostThumbnail
                          imageUrl={post.image_url}
                          logoUrl={post.publication_logo_url}
                          transformImageUrl={transformImageUrl}
                          imageWidth={96}
                          logoWidth={96}
                          className="size-12 shrink-0 rounded-lg"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-black line-clamp-2">{post.web_title}</p>
                          {post.web_subtitle && (
                            <p className="text-xs text-black/60 line-clamp-1 mt-0.5">{post.web_subtitle}</p>
                          )}
                          {author?.name && <p className="text-xs text-black/50 mt-0.5 truncate">{author.name}</p>}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </PanelPopover>
  );
};
